import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FiTrash2, FiUploadCloud } from 'react-icons/fi';
import * as Yup from 'yup';

import AuthInput from '@/components/auth/AuthInput';
import { MAX_IMAGE_FILE_SIZE } from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { useUserStore } from '@/hooks/useUserStore';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const MerchantAddModal = ({ defaultClose }: { defaultClose: () => void }) => {
	const { user, refetch } = useUser();
	const { store, refetch: reFetchStore } = useUserStore();
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [initialImage, setInitialImage] = useState<string | null>(
		store?.tradeLogoUrl || null,
	);

	// Update initial image when props change
	useEffect(() => {
		setInitialImage(store?.tradeLogoUrl || null);
	}, [store]);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const file = e.target.files[0];
		if (!file) return;
		if (file.size > MAX_IMAGE_FILE_SIZE) {
			notificationService.error('File size must be less than 5MB');
			e.target.value = '';
			return;
		}
		setImageFile(file);
	};
	const handleImageDelete = () => {
		// If there's an uploaded file, remove it first
		if (imageFile) {
			setImageFile(null);
		} else if (initialImage) {
			// If no uploaded file but there's an initial image, remove the initial image
			setInitialImage(null);
		}
	};

	return (
		<div className="p-6 bg-white rounded-lg shadow-lg">
			<h2 className="text-xl font-semibold mb-4">Update Merchant</h2>
			<Formik
				initialValues={{
					industry: user?.industry || '',
					subIndustry: user?.subIndustry || '',
					tradeLogoUrl: store?.tradeLogoUrl || null,
					storeId: store?.id,
					storeInfo: store?.storeInfo || null,
					businessName: user?.businessName || '',
				}}
				validationSchema={Yup.object({
					industry: Yup.string().nullable(),
					subIndustry: Yup.string().nullable(),
					tradeLogoUrl: Yup.string().nullable(),
					storeId: Yup.number().required('required'),
					storeInfo: Yup.string().nullable(),
					businessName: Yup.string().nullable(),
				})}
				onSubmit={async (values) => {
					if (imageFile) {
						const image = await haggleApiService.uploadImage(imageFile);
						values.tradeLogoUrl = image;
					} else if (initialImage === null) {
						values.tradeLogoUrl = null;
					} else if (initialImage) {
						// If initial image exists, keep it
						values.tradeLogoUrl = initialImage;
					}
					const sanitizedValues = Object.fromEntries(
						Object.entries(values).map(([key, value]) => [
							key,
							value === '' ? null : value,
						]),
					);

					if (user?.inventoryType !== InventoryType.CSV) {
						delete sanitizedValues.businessName;
					}

					haggleApiService
						.updateMerchantInfo(sanitizedValues)
						.then(() => {
							notificationService.success('Merchant Info Updated');
							refetch();
							reFetchStore();
							defaultClose();
						})
						.catch((e) => {
							notificationService.error('Error updating profile: ' + e.message);
						});
				}}
			>
				{({ errors, touched }) => (
					<Form className="flex flex-col">
						{user?.inventoryType === InventoryType.CSV && (
							<AuthInput
								name="businessName"
								label="Business Name"
								type="text"
								error={!!errors.businessName}
								touched={touched.businessName}
							/>
						)}
						<AuthInput
							name="industry"
							label="Industry"
							type="text"
							error={!!errors.industry}
							touched={touched.industry}
							length={24}
						/>
						<AuthInput
							name="subIndustry"
							label="Sub-Industry"
							type="text"
							error={!!errors.subIndustry}
							touched={touched.subIndustry}
							length={24}
						/>
						<AuthInput
							name="storeInfo"
							label="Store Additional Info"
							type="textarea"
							error={!!errors.storeInfo}
							touched={touched.storeInfo}
						/>
						<div className="mb-8">
							<h3 className="font-medium mb-2">Trade Logo</h3>
							<div className="flex items-center gap-3 w-full justify-start">
								{!imageFile && !initialImage && (
									<label className="flex items-center gap-4 cursor-pointer">
										<div className="flex items-center justify-center w-12 h-12 rounded-full border border-gray-300 text-gray-500">
											<FiUploadCloud size={24} />
										</div>
										<div>
											<p className="text-sm font-medium text-gray-700">
												Upload image
											</p>
											<p className="text-xs text-gray-400">
												{imageFile ? 'Image selected' : 'Select image'}
											</p>
										</div>
										<input
											type="file"
											accept="image/*"
											className="hidden"
											onChange={handleImageChange}
											aria-label="Upload product image"
										/>
									</label>
								)}
							</div>
							{/* Show existing image or new selected image */}
							{(imageFile || initialImage) && (
								<div className="mt-4 flex gap-4 flex-wrap w-full justify-start">
									<div className="relative group">
										<img
											src={
												imageFile
													? URL.createObjectURL(imageFile)
													: initialImage || ''
											}
											alt={`Product preview`}
											className="w-32 h-32 object-contain rounded-lg border"
										/>
										<button
											type="button"
											onClick={() => handleImageDelete()}
											className="absolute top-1 right-1 bg-white bg-opacity-80 rounded-full p-1 text-black hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
											aria-label={`Delete image`}
											tabIndex={0}
										>
											<FiTrash2 />
										</button>
									</div>
								</div>
							)}
						</div>

						<div className="flex gap-4 mt-4">
							<button
								type="submit"
								className="px-5 py-3 text-center text-sm font-medium bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900 text-white"
							>
								Save Changes
							</button>

							<button
								type="button"
								onClick={defaultClose}
								className="border px-5 py-3 text-center text-sm font-medium rounded-md hover:bg-gray-100"
							>
								Cancel
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default MerchantAddModal;
